import dayjs from "dayjs";
import type { Datafeed, SymbolInfo } from "@ortex-charts/financial";
import { zonedTimeToUtc, type Bar } from "@ortex-charts/math";
import { logoUrl, type LogoUrlOptions } from "@/lib/logos";
import { getChartDataNew } from "@/controllers/chart-data";
import { findInstruments } from "@/controllers/find-instruments";
import { getStockInfoOnClient } from "@/controllers/stock";
import { createBarStore, type BarStore } from "./barStore";

// The chart's symbol button and legend fall back to their icon on `onerror` only; a blank 200
// would leave an empty slot where the icon belongs, so the chart keeps the JSON 404.
const CHART_LOGO_OPTS: LogoUrlOptions = { missAs: "error" };

/**
 * The ORTEX platform as a datafeed for ORTEX Charts, on the same endpoints the
 * TradingView datafeed used: symbol metadata from /API/v2/s/, daily bars from
 * /interface/api/tradingitem/{id}/chart/ohlcv, intraday history and live
 * candles over the /stock_price WebSocket the app already holds open.
 *
 * Symbols are `EXCHANGE:TICKER`. Intraday resolutions come from the socket's
 * `last_trades_ext_history` reply (one-minute candles, folded by the chart to
 * the requested resolution); daily and coarser from the REST endpoint, which
 * ends at the last close. Today's candle is not part of that answer: it comes
 * through the realtime subscription, built from the socket's minute history
 * and kept current by the trade stream, so the history a reader is waiting
 * for never waits on the socket (see `subscribeBars`).
 */
type LT = [{ o: number; h: number; l: number; c: number }, number][];

const GB_CURRENCIES = ["GBP", "GBX"];
const INTRADAY = new Set(["1", "5", "15", "30", "60", "120", "240", "360", "480"]);
export const ORTEX_RESOLUTIONS = ["1", "5", "15", "30", "60", "120", "240", "1D", "1W", "1M"];

interface Resolved {
    info: SymbolInfo;
    tradingitemId: number | null;
    identifiers: string[];
    live: boolean;
    exchange: string;
    ticker: string;
}

/** Venue identifiers the socket knows the instrument by. */
function identifiersFor(stock: { livePriceFeed?: string; isin?: string; currency?: string; ticker: string }): string[] {
    if (stock.livePriceFeed === "NA") return [stock.ticker];
    if (stock.livePriceFeed !== "EU") return [];
    if (GB_CURRENCIES.includes(stock.currency ?? "")) return [`${stock.isin}GBP`, `${stock.isin}GBX`];
    return [`${stock.isin}${stock.currency ?? ""}`];
}

function sendWhenOpen(ws: WebSocket, message: unknown): void {
    const payload = JSON.stringify(message);
    if (ws.readyState === WebSocket.OPEN) ws.send(payload);
    else ws.addEventListener("open", () => ws.send(payload), { once: true });
}

/**
 * One socket request that answers with `{ LT: [...] }`; resolves with the
 * candles, or [] after `timeoutMs`.
 *
 * The daily history is asked for in its `_w_ticker` form: the server labels
 * that reply with the identifier it is for, so a reply meant for another
 * component (the ticker tape asks the same question about other symbols) is
 * not taken for this one. It is also the form production answers. Measured on
 * 2026-09-10, plain `last_trades_history` got no reply at all, and the chart
 * sat out the whole timeout with the bars already in hand before it drew.
 */
function requestHistory(ws: WebSocket | null, type: "last_trades_history_w_ticker" | "last_trades_ext_history", identifiers: string[], timeoutMs = 8000): Promise<LT> {
    if (!ws || !identifiers.length) return Promise.resolve([]);
    return new Promise((resolve) => {
        let settled = false;
        const done = (lt: LT) => {
            if (settled) return;
            settled = true;
            ws.removeEventListener("message", onMessage);
            resolve(lt);
        };
        const onMessage = (ev: MessageEvent) => {
            try {
                const parsed = JSON.parse(ev.data);
                if (!Array.isArray(parsed?.LT)) return;
                if (parsed.ticker != null && !identifiers.includes(String(parsed.ticker))) return;
                done(parsed.LT as LT);
            } catch {
                // not JSON: not ours
            }
        };
        ws.addEventListener("message", onMessage);
        identifiers.forEach((identifier) => sendWhenOpen(ws, { type, identifier }));
        setTimeout(() => done([]), timeoutMs);
    });
}

function ltToBars(lt: LT): Bar[] {
    return lt.map(([{ o, h, l, c }, time]) => ({ time, open: o, high: h, low: l, close: c, volume: 0 })).sort((a, b) => a.time - b.time);
}

export interface OrtexDatafeedOptions {
    /** The app's /stock_price socket; without it the feed is history only. */
    socket?: WebSocket | null;
    /** Read per bar request: true asks the platform for dividend-adjusted prices (the ADJ toggle). */
    adjusted?: () => boolean;
    /** Called with the instrument's tradingitem id once a symbol resolves. */
    onResolved?: (info: SymbolInfo, tradingitemId: number | null, stock: Record<string, unknown>) => void;
    /**
     * The instrument the page was rendered for, if it has one.
     *
     * A stock page already knows its stock — the server sent it — and asking
     * the API for it again is a round trip in front of the first bar the
     * reader is waiting for. Seeded here, the first `getBars` goes out
     * immediately instead of after a lookup whose answer was already on the
     * page.
     */
    seed?: { symbol: string; stock: Record<string, unknown> } | null;
    /**
     * Where fetched and in-flight bars are kept. A chart that may create its
     * datafeed more than once passes one store to all of them, so a request
     * started on one is not made again on the next (see `barStore.ts`).
     */
    bars?: BarStore;
}

const PRIMARY_EXCHANGES = new Set(["NASDAQ", "NYSE", "AMEX", "NYSEARCA", "NYSEAMERICAN", "BATS"]);
const MAJOR_EXCHANGES = new Set(["LSE", "XETRA", "FRA", "TSX", "ASX", "TSE", "HKEX", "SIX", "EURONEXT", "EPA", "AMS", "BIT", "BME", "OMX", "STO", "OSL", "CPH", "HEL", "NSE", "BSE", "SGX", "KRX", "TWSE", "JSE", "SSE", "SZSE"]);

const DAY_MS = 86_400_000;

/**
 * A platform date moved to midnight in the instrument's own zone.
 *
 * The platform stamps a daily row, a data point or an event with midnight UTC
 * on its date. The chart places, labels and floors a daily bar at midnight in
 * the exchange's zone, so a New York bar left at UTC midnight sits at 20:00 the
 * evening before: every candle reads a day early and Monday's candle lands on
 * Sunday. Anything not exactly on a UTC midnight is a real instant and is
 * returned untouched.
 */
export function atExchangeMidnight(time: number, timeZone: string): number {
    if (time % DAY_MS !== 0) return time;
    const d = new Date(time);
    return zonedTimeToUtc(timeZone, d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

export function createOrtexDatafeed(options: OrtexDatafeedOptions = {}): Datafeed & { resolved(symbol: string): Resolved | undefined } {
    const cache = new Map<string, Resolved>();
    const socket = options.socket ?? null;
    /**
     * Bars already fetched, keyed by everything that changes them: the symbol,
     * the resolution and the adjusted flag, each held with the window asked for.
     *
     * This is here for the ADJ button. Toggling it re-fetches the whole series
     * from the platform, and toggling it back used to fetch the first series
     * again — two or three seconds of waiting to see something the browser had
     * already had. Five minutes is long enough for a reader comparing the two
     * and short enough that a chart left open still catches up.
     *
     * Requests still in flight are shared too: the stock page starts the
     * chart's first request alongside the series catalogue, before the chart
     * exists, and the chart's own first request then waits on that one instead
     * of going out again (#1324).
     */
    const bars = options.bars ?? createBarStore();
    /**
     * Today's candle from the socket's minute history, one request per symbol
     * and exchange day. It does not depend on the adjusted flag — an adjustment
     * factor for today is 1 — so both sides of the toggle share one, and
     * toggling costs no socket round trip at all. A promise rather than a
     * value, so a subscription and a reload that overlap wait on one request.
     */
    const todayCache = new Map<string, Promise<Bar | null>>();
    const lastRestBar = bars.lastRestBar;

    /** Midnight of the exchange's current calendar day in its own zone: where `getBars` puts a daily bar. */
    const dayStartOf = (timeZone: string | undefined): number => atExchangeMidnight(dayjs.utc(dayjs().tz(timeZone).format("YYYY-MM-DD")).valueOf(), timeZone ?? "UTC");

    /** One bar for the exchange day that starts at `dayStart`, from the socket's minute candles of that day; null when it has none. */
    function todayBar(r: Resolved, dayStart: number): Promise<Bar | null> {
        const key = `${r.info.symbol}|${dayStart}`;
        let p = todayCache.get(key);
        if (!p) {
            // Keyed by day, so a chart kept open across days and symbols collects entries; keep only the latest few.
            while (todayCache.size >= 8) todayCache.delete(todayCache.keys().next().value as string);
            // The exchange day as two instants, worked out once: up to 4000 minute candles are filtered
            // against it, and a zoned isSame(..., "day") per candle cost seconds on a phone.
            const today = dayjs().tz(r.info.timeZone);
            const from = today.startOf("day").valueOf();
            const to = today.add(1, "day").startOf("day").valueOf();
            p = requestHistory(socket, "last_trades_history_w_ticker", r.identifiers, 4000).then((lt) => {
                const todays = ltToBars(lt).filter((b) => b.time >= from && b.time < to);
                if (!todays.length) return null;
                return { time: dayStart, open: todays[0].open, high: Math.max(...todays.map((b) => b.high)), low: Math.min(...todays.map((b) => b.low)), close: todays[todays.length - 1].close, volume: 0 };
            });
            todayCache.set(key, p);
        }
        return p;
    }

    function resolvedFrom(symbol: string, stock: Record<string, unknown>): Resolved {
        const [exchange, ticker] = symbol.split(":");
        const s = stock as Record<string, unknown> & { ticker: string; name?: string; exchange_tz?: string; exchange_th?: string; currency?: string; isin?: string; livePriceFeed?: string; tradingitem_id?: number | null; logo?: string };
        const info: SymbolInfo = {
            symbol,
            name: s.name ?? ticker,
            exchange,
            logo: logoUrl({ isin: s.isin ?? null, ortexSid: (s.ortex_sid as number | null | undefined) ?? null, ticker, exchangeSymbol: exchange }, "square", CHART_LOGO_OPTS) ?? undefined,
            timeZone: s.exchange_tz || "America/New_York",
            session: s.exchange_th || "0400-0930,0930-1600,1600-2000",
            currency: s.currency,
            priceFormat: { type: "price", precision: 2, minMove: 0.01 },
        };
        const identifiers = identifiersFor({ ...s, ticker });
        return { info, tradingitemId: s.tradingitem_id ?? null, identifiers, live: identifiers.length > 0 && Boolean(socket), exchange, ticker };
    }

    if (options.seed?.symbol && options.seed.stock) {
        const seeded = resolvedFrom(options.seed.symbol, options.seed.stock);
        cache.set(options.seed.symbol, seeded);
        options.onResolved?.(seeded.info, seeded.tradingitemId, options.seed.stock);
    }

    /**
     * Lookups already in flight, keyed by symbol.
     *
     * The library resolves a symbol once per series it puts on a chart -- the
     * main one, then each study or comparison -- and those calls overlap. The
     * resolved cache below only stops the second request once the first has
     * come back, so without this the same `/API/v2/s/<exchange>/<ticker>/` was
     * fetched two or three times on a cold load. Storing the promise makes the
     * later callers wait on the first request instead. A rejection is dropped,
     * so a transient failure is retried rather than remembered.
     */
    const resolving = new Map<string, Promise<Resolved>>();

    async function resolve(symbol: string): Promise<Resolved> {
        const hit = cache.get(symbol);
        if (hit) return hit;
        const inFlight = resolving.get(symbol);
        if (inFlight) return inFlight;
        const p = resolveOnce(symbol);
        resolving.set(symbol, p);
        try {
            return await p;
        } finally {
            resolving.delete(symbol);
        }
    }

    async function resolveOnce(symbol: string): Promise<Resolved> {
        const [exchange, ticker] = symbol.split(":");
        const stock = await getStockInfoOnClient(exchange, ticker);
        const s = stock as unknown as Record<string, unknown> & { ticker: string; name?: string; exchange_tz?: string; exchange_th?: string; currency?: string; isin?: string; livePriceFeed?: string; tradingitem_id?: number | null; logo?: string };
        const info: SymbolInfo = {
            symbol,
            name: s.name ?? ticker,
            exchange,
            logo: logoUrl({ isin: s.isin ?? null, ortexSid: (s.ortex_sid as number | null | undefined) ?? null, ticker, exchangeSymbol: exchange }, "square", CHART_LOGO_OPTS) ?? undefined,
            timeZone: s.exchange_tz || "America/New_York",
            session: s.exchange_th || "0400-0930,0930-1600,1600-2000",
            currency: s.currency,
            priceFormat: { type: "price", precision: 2, minMove: 0.01 },
        };
        const identifiers = identifiersFor({ ...s, ticker });
        const resolved: Resolved = { info, tradingitemId: s.tradingitem_id ?? null, identifiers, live: identifiers.length > 0 && Boolean(socket), exchange, ticker };
        cache.set(symbol, resolved);
        options.onResolved?.(info, resolved.tradingitemId, s);
        return resolved;
    }

    return {
        resolved: (symbol) => cache.get(symbol),

        async resolveSymbol(symbol) {
            return (await resolve(symbol)).info;
        },

        async getBars({ symbol, resolution, from, to, firstRequest }) {
            const r = await resolve(symbol);
            const adjusted = options.adjusted?.() === true;
            const key = `${symbol}|${resolution}|${adjusted}`;
            const cached = bars.held(key, from, to);
            if (cached) return cached;
            if (INTRADAY.has(resolution)) {
                // The socket keeps about a week of one-minute candles; the chart folds them.
                if (!firstRequest) return { bars: [], noMoreData: true };
                const lt = await requestHistory(socket, "last_trades_ext_history", r.identifiers);
                return { bars: ltToBars(lt), noMoreData: true };
            }
            const tradingitemId = r.tradingitemId;
            if (!tradingitemId) return { bars: [], noMoreData: true };
            return bars.share(key, from, to, async () => {
                const rows = (await getChartDataNew(tradingitemId, "ohlcv", dayjs(from).format("YYYY-MM-DD"), dayjs(to).format("YYYY-MM-DD"), adjusted)) ?? [];
                const fetched: Bar[] = rows.map(([time, open, high, low, close, volume]) => ({ time: atExchangeMidnight(time, r.info.timeZone), open: open || close, high: high || close, low: low || close, close, volume: volume ?? 0 }));
                // The REST series ends at the last close. Today's candle used to be
                // fetched here, from the socket, before these bars were returned, and
                // when the socket did not answer the chart stayed blank for the whole
                // four-second timeout with the bars already in hand. It now arrives
                // through `subscribeBars`, which the chart opens as soon as it has
                // drawn these, so the bars a reader is waiting for no longer wait.
                if (firstRequest) lastRestBar.set(`${symbol}|${resolution}`, fetched.length ? fetched[fetched.length - 1].time : -Infinity);
                const noMoreData = fetched.length === 0 && dayjs(to).diff(dayjs(from), "day") > 1;
                return { bars: fetched, noMoreData };
            });
        },

        subscribeBars(symbol, resolution, onBar) {
            const r = cache.get(symbol);
            if (!r || !socket || !r.live) return () => {};
            const ids = new Set(r.identifiers.map((i) => (i.endsWith("GBP") || i.endsWith("GBX") ? i.slice(-3) : i)));
            let alive = true;
            // An intraday chart takes the socket's minute candles as they come. A
            // daily or coarser chart gets one bar for today, stamped the way the
            // REST series stamps its days: first from the minute history, then kept
            // current by every trade, and the chart replaces it in place each time.
            // When the REST series already carries today (the close has been
            // loaded), that bar is the official one and the stream leaves it alone.
            const daily = !INTRADAY.has(resolution);
            const today = dayjs().tz(r.info.timeZone);
            const dayStart = dayStartOf(r.info.timeZone);
            const restHasToday = (lastRestBar.get(`${symbol}|${resolution}`) ?? -Infinity) >= dayStart;
            let todays: Bar | null = null;
            const emitToday = () => {
                if (alive && todays) onBar(todays);
            };
            if (daily && !restHasToday) {
                void todayBar(r, dayStart).then((bar) => {
                    if (!alive || !bar) return;
                    // Trades may have arrived first: the history supplies the open and widens the range, the trades keep the close.
                    todays = todays ? { ...todays, open: bar.open, high: Math.max(todays.high, bar.high), low: Math.min(todays.low, bar.low) } : bar;
                    emitToday();
                });
            }
            const onMessage = (ev: MessageEvent) => {
                try {
                    const parsed = JSON.parse(ev.data);
                    const t = parsed?.T;
                    if (!t || !ids.has(t.i)) return;
                    const { o, h, l, c } = t.C;
                    if (!daily) {
                        onBar({ time: t.T, open: o, high: h, low: l, close: c, volume: 0 });
                        return;
                    }
                    if (restHasToday || !dayjs(t.T).tz(r.info.timeZone).isSame(today, "day")) return;
                    todays = todays ? { ...todays, high: Math.max(todays.high, h), low: Math.min(todays.low, l), close: c } : { time: dayStart, open: o, high: h, low: l, close: c, volume: 0 };
                    emitToday();
                } catch {
                    // not ours
                }
            };
            socket.addEventListener("message", onMessage);
            r.identifiers.forEach((identifier) => sendWhenOpen(socket, { type: "subscribe", identifier }));
            return () => {
                alive = false;
                socket.removeEventListener("message", onMessage);
                r.identifiers.forEach((identifier) => {
                    if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: "unsubscribe", identifier }));
                });
            };
        },

        async searchSymbols(query) {
            if (!query.trim()) return [];
            const found = await findInstruments(query);
            const q = query.trim().toUpperCase();
            // The platform answers alphabetically; a trader typing "ford" means NYSE:F, not the
            // Vienna listing whose ticker happens to be FORD, so the venue ranks first and the
            // quality of the match orders the listings within it.
            const rank = (f: { ticker?: string; exchange?: string; name?: string }) => {
                const t = (f.ticker ?? "").toUpperCase();
                const n = (f.name ?? "").toUpperCase();
                const match = t === q ? 0 : t.startsWith(q) ? 1 : n.startsWith(q) ? 2 : n.includes(q) ? 3 : 4;
                const ex = (f.exchange ?? "").toUpperCase();
                const venue = PRIMARY_EXCHANGES.has(ex) ? 0 : MAJOR_EXCHANGES.has(ex) ? 1 : 2;
                return venue * 10 + match;
            };
            return found
                .slice()
                .sort((a, b) => rank(a) - rank(b))
                .slice(0, 30)
                .map((f) => ({ symbol: `${f.exchange}:${f.ticker}`, name: f.name, exchange: f.exchange }));
        },
    };
}
